import React from 'react';
import { Spinner, Table } from 'reactstrap';
import moment from 'moment';
import {
  getPaymentFailedStatusChipClass,
  getPaymentFailedStatusShortLabel,
  getPaymentFailedTrigger,
  isUrgentTrigger,
} from '../../../constants/paymentFailedOrders';

const formatDate = (value) =>
  value ? moment(new Date(value)).format('DD MMM YY') : '-';

const formatTime = (value) => (value ? moment(new Date(value)).format('HH:mm') : '');

// Open while there is no closed date, then Converted or Closed
const StateBadge = ({ closedAt, outcomeType }) => {
  if (!closedAt) return <span className="pf-chip pf-chip-open">Open</span>;
  if (outcomeType === 'converted') {
    return <span className="pf-chip pf-chip-converted">Converted</span>;
  }
  return <span className="pf-chip pf-chip-closed-neutral">Closed</span>;
};

const TriggerBadge = ({ triggerReason }) => {
  const trigger = getPaymentFailedTrigger(triggerReason);
  if (!trigger) return <span className="pf-cell-muted">-</span>;
  if (!trigger.urgent) {
    return <span className="pf-badge-standard">{trigger.shortLabel}</span>;
  }
  return (
    <span className="pf-badge-urgent" title={trigger.hint}>
      Urgent · {trigger.shortLabel}
    </span>
  );
};

const PaymentFailedOrdersList = ({ rows, isLoading, error, onRowClick, startIndex }) => {
  const columnCount = 13;

  return (
    <div className="table-responsive">
      <Table className="pf-table table mb-0" hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Trigger</th>
            <th>Reference No</th>
            <th>Country</th>
            <th>Customer</th>
            <th className="text-end">Amount</th>
            <th>Payment</th>
            <th>Failure Reason</th>
            <th>Action Status</th>
            <th>Created</th>
            <th>Last Updated</th>
            <th>State</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={columnCount} className="text-center py-4">
                <Spinner size="sm" color="primary" />
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={columnCount} className="text-center py-4 text-danger">
                {error}
              </td>
            </tr>
          ) : rows?.length > 0 ? (
            rows.map((row, index) => {
              const closed = Boolean(row?.closed_at);
              const urgent = !closed && isUrgentTrigger(row?.trigger_reason);
              const rowClass = closed
                ? 'pf-row-closed'
                : urgent
                ? 'pf-row-urgent'
                : '';
              return (
                <tr
                  key={row?.id || `${row?.reference_no}-${index}`}
                  className={rowClass}
                >
                  <td className="pf-cell-muted">{startIndex + index + 1}</td>
                  <td>
                    <TriggerBadge triggerReason={row?.trigger_reason} />
                  </td>
                  <td className="fw-semibold">{row?.reference_no || '-'}</td>
                  <td>{(row?.country || '-').toUpperCase()}</td>
                  <td className="pf-cell-wrap">
                    <div className="fw-semibold">{row?.full_name || '-'}</div>
                    <div className="pf-cell-muted">
                      {row?.phone}
                      {row?.email ? ` · ${row.email}` : ''}
                    </div>
                  </td>
                  <td className="text-end fw-semibold" nowrap="nowrap">
                    {row?.currency || ''} {row?.grand_total ?? '-'}
                  </td>
                  <td>
                    <div>{row?.payment_mode || '-'}</div>
                    <div className="pf-cell-muted">{row?.gateway}</div>
                  </td>
                  <td className="pf-cell-wrap">
                    {row?.payment_failure_reason || (
                      <span className="pf-cell-muted">Abandoned before payment</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`pf-chip ${getPaymentFailedStatusChipClass(
                        row?.action_status,
                      )}`}
                    >
                      {getPaymentFailedStatusShortLabel(row?.action_status)}
                    </span>
                    {row?.resolved_order_no && (
                      <div className="pf-cell-muted">→ {row.resolved_order_no}</div>
                    )}
                  </td>
                  <td nowrap="nowrap">
                    {formatDate(row?.order_created_at)}
                    <div className="pf-cell-muted">{formatTime(row?.order_created_at)}</div>
                  </td>
                  <td nowrap="nowrap">
                    {row?.updated_at ? formatDate(row.updated_at) : '-'}
                    <div className="pf-cell-muted">{row?.updated_by_username}</div>
                  </td>
                  <td nowrap="nowrap">
                    <StateBadge
                      closedAt={row?.closed_at}
                      outcomeType={row?.outcome_type}
                    />
                    {closed && (
                      <div className="pf-cell-muted">{formatDate(row.closed_at)}</div>
                    )}
                  </td>
                  <td>
                    <div className="action-flex-container">
                      <span
                        className="btn btn-sm btn-outline-warning waves-effect waves-light"
                        title="View Details"
                        onClick={() => onRowClick(row)}
                      >
                        <i className="mdi mdi-eye-outline"></i>
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columnCount} className="text-center py-4">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default PaymentFailedOrdersList;
